import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { chartColors } from '@shared/lib/chart-theme';
import { ChartTooltip } from '@features/analytics/ui/ChartTooltip';
import { cn } from '@shared/lib/cn';
import {
  formatCount,
  formatPercent,
  TIERS,
  type EngagementOverview,
  type EngagementTier,
} from '@features/engagement/model/engagement.types';

interface TierDonutProps {
  overview: EngagementOverview;
  selected: EngagementTier[];
  onChange: (tiers: EngagementTier[]) => void;
}

/**
 * Where the user base sits on the engagement ladder, as a ring.
 *
 * Part-to-whole across five ordered rungs, which is the one job a donut does
 * well — and the job here, because the finding is "most of them never started",
 * not a comparison between the small rungs. The ring carries that at a glance;
 * the legend beside it carries every exact figure, so nothing is colour-only.
 *
 * The legend is also the filter: clicking a rung narrows the list below.
 */
export function TierDonut({ overview, selected, onChange }: TierDonutProps) {
  const active = new Set(selected);

  const segments = TIERS.map((tier, index) => ({
    ...tier,
    count: overview.byTier[tier.value] ?? 0,
    // Colour follows the rung's position on the ladder, assigned once — never
    // recomputed from rank, so filtering cannot repaint the survivors.
    color: chartColors.ordinal[Math.min(index, chartColors.ordinal.length - 1)] as string,
  }));

  const toggle = (tier: EngagementTier) => {
    const next = new Set(active);

    if (next.has(tier)) {
      next.delete(tier);
    } else {
      next.add(tier);
    }

    // Ladder order, not click order, so the URL stays stable.
    onChange(TIERS.map((entry) => entry.value).filter((value) => next.has(value)));
  };

  // Recharts drops zero-value slices; they still belong in the legend.
  const drawn = segments.filter((segment) => segment.count > 0);

  return (
    <div className="flex items-center gap-4">
      <div className="relative h-[132px] w-[132px] shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Tooltip content={<ChartTooltip />} />
            <Pie
              data={drawn}
              dataKey="count"
              nameKey="label"
              innerRadius={44}
              outerRadius={64}
              // The gap and the white ring are the 2px surface separation
              // between adjacent fills, not a border drawn around them.
              paddingAngle={2}
              stroke="#fff"
              strokeWidth={2}
              isAnimationActive={false}
            >
              {drawn.map((segment) => (
                <Cell
                  key={segment.value}
                  fill={segment.color}
                  opacity={active.size > 0 && !active.has(segment.value) ? 0.35 : 1}
                />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>

        {/* The total belongs in the hole: it is what the ring adds up to. */}
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-xl font-semibold tracking-tight text-slate-900">
            {formatCount(overview.users)}
          </span>
          <span className="text-[10px] uppercase tracking-wide text-slate-400">users</span>
        </div>
      </div>

      <ul className="min-w-0 flex-1 space-y-0.5">
        {segments.map((segment) => (
          <li key={segment.value}>
            <button
              type="button"
              onClick={() => toggle(segment.value)}
              aria-pressed={active.has(segment.value)}
              title={segment.description}
              className={cn(
                'flex w-full items-center gap-2 rounded-md px-1.5 py-1 text-left text-xs transition-colors',
                'hover:bg-slate-50',
                active.has(segment.value) && 'bg-slate-100 ring-1 ring-inset ring-slate-300',
              )}
            >
              <span
                aria-hidden
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: segment.color }}
              />
              <span className="min-w-0 flex-1 truncate text-slate-700">{segment.label}</span>
              <span className="shrink-0 font-semibold tabular-nums text-slate-900">
                {formatCount(segment.count)}
              </span>
              <span className="w-9 shrink-0 text-right tabular-nums text-slate-400">
                {formatPercent(overview.users > 0 ? segment.count / overview.users : 0)}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
